import React, { useEffect, useState } from "react";
import { Response } from "react-select-async-paginate";

import { getList } from "../../api/generics";
import TaxPayer from "../../models/tax-payer";
import AsyncPaginateBootstrap from "../react-select-bootstrap/AsyncPaginateBootstrap";

const RECORDS_PER_SEARCH = 20;

type TaxPayerSelectProps = {
  taxPayerId?: number;
  onChange?: (_: TaxPayer | null) => void;
};

const taxPayerLabel = (taxPayer: TaxPayer) =>
  `${taxPayer.ruc ?? ""} - ${taxPayer.socialName ?? ""}`;

/**
 * Optional tax payer picker. Leaving it empty scopes the notification to every
 * tax payer, which is why it is clearable.
 */
const TaxPayerSelect: React.FC<TaxPayerSelectProps> = ({
  taxPayerId,
  onChange,
}) => {
  const [selected, setSelected] = useState<TaxPayer | null>(null);
  const [loaded, setLoaded] = useState(false);

  const onTaxPayerChange = (newTaxPayer: TaxPayer | null) => {
    setSelected(newTaxPayer);
    if (onChange !== undefined) {
      onChange(newTaxPayer);
    }
  };

  const loadTaxPayerOptions = async (
    s: string,
    currentOpts: readonly (TaxPayer | { options: TaxPayer[]; label?: string })[]
  ): Promise<
    Response<TaxPayer, { options: TaxPayer[]; label?: string }, any>
  > => {
    let additionalParams: Map<string, string> | undefined = undefined;

    if (s !== "") {
      additionalParams = new Map();
      additionalParams.set("search", s);
    }

    const taxPayersPaginated = await getList<TaxPayer>(
      "/tax_payers/all/",
      RECORDS_PER_SEARCH,
      currentOpts.length,
      additionalParams
    );

    let newOpts: TaxPayer[] = [];
    let count = 0;

    if (taxPayersPaginated.data !== undefined) {
      newOpts = taxPayersPaginated.data.items;
      count = taxPayersPaginated.data.count;
    }

    return {
      options: newOpts,
      hasMore: currentOpts.length + newOpts.length < count,
    };
  };

  // Resolve the label for a notification that is already scoped to a tax payer.
  useEffect(() => {
    const loadSelected = async () => {
      if (taxPayerId === undefined) {
        setSelected(null);
        setLoaded(true);
        return;
      }

      const params = new Map<string, string>();
      params.set("id", taxPayerId.toString());

      const taxPayers = await getList<TaxPayer>("/tax_payers/all/", 1, 0, params);

      if (taxPayers.data !== undefined && taxPayers.data.items.length > 0) {
        setSelected(taxPayers.data.items[0]);
      }
      setLoaded(true);
    };

    setLoaded(false);
    loadSelected();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPayerId]);

  if (!loaded) {
    return null;
  }

  return (
    <AsyncPaginateBootstrap
      placeholder={"Todos los contribuyentes"}
      onChange={onTaxPayerChange}
      isMulti={false}
      isClearable
      debounceTimeout={300}
      loadOptions={loadTaxPayerOptions}
      getOptionLabel={(option: TaxPayer) => taxPayerLabel(option)}
      getOptionValue={(option: TaxPayer) => `${option.id!}`}
      defaultValue={selected}
    />
  );
};

export default TaxPayerSelect;
